import SoftBadge from 'components/common/SoftBadge';
import CardDropdown from 'components/common/CardDropdown';
import DonorCounter from 'components/donor/DonorCounter';
import { weDonationItems } from 'data/weraise-data/donor-data';
import { useState } from 'react';
import { Col, Dropdown, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import paths from 'routes/paths';
import { parseRouteParams } from 'helpers/utils';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import ExportModal from 'components/common/ExportModal';
import {
  permissionsKeyMap,
  userActions
} from 'routes/permissions/permissionKeys';
import { Can } from 'routes/permissions/Can';
import {
  usePackages,
  useDeletePackage,
  useUpdatePackage
} from 'hooks/useDonationSettings';

export const PACKAGE_STATUS_ACTIVE = 'active';
export const PACKAGE_STATUS_INACTIVE = 'inactive';

const packageStatusMap = {
  [PACKAGE_STATUS_ACTIVE]: {
    label: 'Active',
    color: 'success'
  },
  [PACKAGE_STATUS_INACTIVE]: {
    label: 'Inactive',
    color: 'warning'
  }
};

const DonationPackages = () => {
  const navigate = useNavigate();
  const { packages, isLoading: loading } = usePackages();
  const { deletePackage, isDeleting: deleteLoading } = useDeletePackage();
  const { updatePackage } = useUpdatePackage();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const handleExport = () => {
    setShowExportModal(true);
  };
  const columns = [
    {
      accessor: 'simple_id',
      Header: 'Reference',
      cellProps: {
        className: 'py-2 fw-medium '
      }
    },
    {
      accessor: 'package_title',
      Header: 'Package Title',
      cellProps: {
        className: 'py-2 fw-medium '
      }
      // Cell: rowData => {
      //   const { package_title } = rowData.row.original;
      //   return (
      //     <Flex alignItems="center">
      //       <Button
      //         variant="link"
      //         // onClick={() => setModalShow(true)}
      //         className="fw-medium fs--1 p-0 text-1100 hover-primary"
      //       >
      //         {package_title}
      //       </Button>
      //     </Flex>
      //   );
      // }
    },
    {
      accessor: 'package_description',
      Header: 'Description',
      cellProps: {
        className: 'py-2 fw-medium'
      },
      Cell: rowData => (
        <div
          style={{
            maxWidth: '300px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {rowData.row.original.package_description}
        </div>
      )
    },
    {
      accessor: 'status',
      Header: 'Status',
      cellProps: {
        className: 'fw-medium  py-2'
      },
      Cell: rowData => {
        const { status } = rowData.row.original;
        const { label, color } = packageStatusMap[status] || {
          label: '-',
          color: 'muted'
        };
        return status ? (
          <SoftBadge pill bg={color} className="me-2">
            {label}
          </SoftBadge>
        ) : (
          <>&mdash;</>
        );
      }
    },
    {
      accessor: 'is_display',
      Header: 'Display',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { id, is_display } = rowData.row.original;
        return <DisplayToggle id={id} checked={is_display !== false} />;
      }
    },
    {
      accessor: 'id_d',
      Header: '',
      headerProps: {
        className: 'text-end'
      },
      cellProps: {
        className: 'text-end fw-medium py-2'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id, status } = rowData.row.original;
        const isActive = status === PACKAGE_STATUS_ACTIVE;
        const subject =
          permissionsKeyMap.donation.children.donationPackages;
        return (
          <CardDropdown>
            <div className="py-2">
              <Can I={userActions.update} a={subject}>
                <Dropdown.Item
                  onClick={() =>
                    navigate(parseRouteParams(paths.editPackage, { id }))
                  }
                >
                  Edit
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() =>
                    updatePackage({
                      id,
                      data: {
                        status: isActive
                          ? PACKAGE_STATUS_INACTIVE
                          : PACKAGE_STATUS_ACTIVE
                      }
                    })
                  }
                >
                  {isActive ? 'Mark as Inactive' : 'Mark as Active'}
                </Dropdown.Item>
              </Can>
              <Can I={userActions.delete} a={subject}>
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => handleDeleteClick(id)}
                >
                  Delete
                </Dropdown.Item>
              </Can>
            </div>
          </CardDropdown>
        );
      }
    }
  ];
  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };
  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };
  const handleConfirmDelete = async () => {
    try {
      await deletePackage(selectedId);
      handleDeleteModalHide();
    } catch (error) {
      console.error(error);
    }
  };

  const fileName = 'Donation Packages';
  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        loading={deleteLoading}
        title="Delete Package"
        handleConfirm={handleConfirmDelete}
        body="Are you sure you want to delete selected package?"
      />
      <Row>
        <Col>
          <DonorCounter
            counterData={weDonationItems({ packages: packages ?? [] })}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          {packages && (
            <CustomAdvanceTable
              data={packages}
              columns={columns}
              title={'Packages'}
              addButtonLabel="Create New Package"
              showSearchInput
              searchInputPlaceHolder="Search Packages"
              showTablePagination={false}
              perPage={50}
              onClickAddNew={() => navigate(paths.createPackage)}
              loading={loading}
              addNew
              hasExport
              handleExport={handleExport}
              subject={permissionsKeyMap.donation.children.donationPackages}
            />
          )}
        </Col>
      </Row>
      <ExportModal
        data={packages ?? []}
        columns={columns.slice(0, columns.length - 1)}
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        fileName={fileName}
      />
    </>
  );
};

const DisplayToggle = ({ checked, id }) => {
  const { updatePackage, isUpdating } = useUpdatePackage();

  const handleChange = e => {
    updatePackage({
      id,
      data: { is_display: e.target.checked }
    });
  };

  return (
    <Form.Check
      type="switch"
      defaultChecked={checked}
      checked={checked}
      onChange={handleChange}
      disabled={isUpdating}
    />
  );
};

DisplayToggle.propTypes = {
  checked: PropTypes.bool,
  id: PropTypes.string
};

export default DonationPackages;
